import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Textarea,
  Select,
  Box,
  Paper,
  Divider,
  Loader,
  Alert,
  Badge,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { AlertCircle, AlertTriangle, Eye, Tag, ExternalLink, Calendar, Copy, Send } from 'react-feather';
import { toDateValue, toISODateString } from '../../../utils/dateHelpers';
import {
  intakeApi,
  type PaperformForm,
  type SendIntakeRequest,
} from '../../../services/api/intake';
import { notifications } from '@mantine/notifications';
import { useInsertAtCursor } from '../../../hooks';

// Merge tags available for intake emails
const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name', example: 'John' },
  { tag: '{{last_name}}', label: 'Last Name', example: 'Doe' },
  { tag: '{{full_name}}', label: 'Full Name', example: 'John Doe' },
  { tag: '{{email}}', label: 'Email', example: 'john@example.com' },
  { tag: '{{cohort_name}}', label: 'Cohort Name', example: 'Q1 Leadership' },
  { tag: '{{form_link}}', label: 'Form Link', example: 'https://...' },
];

const DEFAULT_SUBJECT = 'Complete Your Intake Form for {{cohort_name}}';
const DEFAULT_BODY = `Hi {{first_name}},

Welcome to {{cohort_name}}! To help us personalize your coaching experience, please complete the intake form below.

This form helps us understand your goals, experience, and preferences so we can match you with the right coach and tailor your sessions accordingly.

Please click the link below to complete your intake form:
{{form_link}}

If you have any questions, please don't hesitate to reach out.

Best regards,
The Medley Team`;

interface ComposeIntakeEmailModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  cohortName: string;
  onSent: () => void;
}

export function ComposeIntakeEmailModal({
  opened,
  onClose,
  cohortId,
  cohortName,
  onSent,
}: ComposeIntakeEmailModalProps) {
  const [forms, setForms] = useState<PaperformForm[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formsConfigured, setFormsConfigured] = useState(true);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number>(0);

  // Cursor tracking for merge tag insertion
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyField = useInsertAtCursor<HTMLTextAreaElement>();

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await intakeApi.listForms(cohortId);
      setFormsConfigured(response.configured);
      setForms(response.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load Paperform forms',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  const fetchRecipientCount = useCallback(async () => {
    try {
      const stats = await intakeApi.getStats(cohortId);
      setRecipientCount(stats.not_sent);
    } catch {
      // Fall back to 0 — confirmation will still show
      setRecipientCount(0);
    }
  }, [cohortId]);

  useEffect(() => {
    if (opened) {
      setSelectedFormId(null);
      setDueDate(null);
      setConfirmOpened(false);
      fetchForms();
      fetchRecipientCount();
    }
  }, [opened, fetchForms, fetchRecipientCount]);

  const handleInsertTag = (tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(tag, () => subject, setSubject);
    } else {
      bodyField.insert(tag, () => body, setBody);
    }
  };

  const handleSend = async () => {
    if (!selectedFormId) {
      notifications.show({
        title: 'Error',
        message: 'Please select a form',
        color: 'red',
      });
      return;
    }

    setSending(true);
    try {
      const request: SendIntakeRequest = {
        form_id: selectedFormId,
        subject: subject || DEFAULT_SUBJECT,
        body: body || DEFAULT_BODY,
        due_date: toISODateString(dueDate),
      };

      const result = await intakeApi.sendIntakeEmails(cohortId, request);

      if (result.sent > 0) {
        onSent();
      } else if (result.failed > 0) {
        notifications.show({
          title: 'Warning',
          message: `Failed to send to ${result.failed} participants`,
          color: 'yellow',
        });
      }
    } catch (error) {
      console.error('Failed to send intake emails:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send intake emails',
        color: 'red',
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!selectedFormId) {
      notifications.show({
        title: 'Error',
        message: 'Please select a form',
        color: 'red',
      });
      return;
    }

    setSendingTest(true);
    try {
      const result = await intakeApi.sendTestEmail(cohortId, {
        form_id: selectedFormId,
        subject: subject || undefined,
        body: body || undefined,
      });
      notifications.show({
        title: 'Test Email Sent',
        message: `Test email sent to ${result.email}`,
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to send test email:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send test email',
        color: 'red',
      });
    } finally {
      setSendingTest(false);
    }
  };

  // Convert merge tags to human-readable placeholders for clipboard
  const convertMergeTagsToPlaceholders = (text: string): string => {
    return text
      .replace(/\{\{first_name\}\}/g, '[First Name]')
      .replace(/\{\{last_name\}\}/g, '[Last Name]')
      .replace(/\{\{full_name\}\}/g, '[Full Name]')
      .replace(/\{\{email\}\}/g, '[Email]')
      .replace(/\{\{cohort_name\}\}/g, '[Cohort Name]')
      .replace(/\{\{form_link\}\}/g, '[Form Link]');
  };

  const handleConfigureAndCopy = async () => {
    if (!selectedFormId) {
      notifications.show({
        title: 'Error',
        message: 'Please select a form',
        color: 'red',
      });
      return;
    }

    setConfiguring(true);
    try {
      // Configure the form (saves to cohort, generates unique URLs for participants)
      await intakeApi.configureForm(cohortId, { form_id: selectedFormId });

      // Convert merge tags to placeholders
      const subjectWithPlaceholders = convertMergeTagsToPlaceholders(subject || DEFAULT_SUBJECT);
      const bodyWithPlaceholders = convertMergeTagsToPlaceholders(body || DEFAULT_BODY);

      // Build clipboard content
      const clipboardContent = `Subject: ${subjectWithPlaceholders}

---

${bodyWithPlaceholders}

---
Note: Replace the following for each participant:
- [First Name] → Participant's first name
- [Form Link] → Unique form URL from participant table`;

      // Copy to clipboard
      await navigator.clipboard.writeText(clipboardContent);

      notifications.show({
        title: 'Success',
        message: 'Form configured and email copied to clipboard',
        color: 'green',
      });

      // Trigger refresh and close modal
      onSent();
    } catch (error) {
      console.error('Failed to configure and copy:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to configure form',
        color: 'red',
      });
    } finally {
      setConfiguring(false);
    }
  };

  const formOptions = forms
    .filter((f) => f.status === 'active')
    .map((f) => {
      const updatedDate = new Date(f.updated_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return {
        value: f.id,
        label: `${f.title} (edited ${updatedDate})`,
      };
    });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text fw={600}>Send Intake Form</Text>
        </Group>
      }
      size="lg"
      centered
    >
      {loading ? (
        <Stack align="center" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">Loading forms...</Text>
        </Stack>
      ) : !formsConfigured ? (
        <Alert color="yellow" icon={<AlertCircle size={16} />}>
          <Text size="sm">
            Paperform integration is not configured. Please contact your administrator to set up the PAPERFORM_API_KEY.
          </Text>
        </Alert>
      ) : forms.length === 0 ? (
        <Alert color="yellow" icon={<AlertCircle size={16} />}>
          <Text size="sm">
            No active Paperform forms found. Please create an intake form in Paperform first.
          </Text>
        </Alert>
      ) : (
        <Stack gap="md">
          <Box>
            <Select
              label="Select Intake Form"
              placeholder="Search or choose a form..."
              data={formOptions}
              value={selectedFormId}
              onChange={setSelectedFormId}
              searchable
              nothingFoundMessage="No forms found"
              maxDropdownHeight={300}
              required
            />
            <Group justify="flex-end" mt={4}>
              <Text
                component="a"
                href="https://paperform.co/dashboard/450432"
                target="_blank"
                rel="noopener noreferrer"
                size="xs"
                c="dimmed"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Manage in Paperform <ExternalLink size={12} />
              </Text>
            </Group>
          </Box>

          <DateInput
            label="Due Date"
            description="Optional deadline for participants to complete the intake form"
            placeholder="Select a due date"
            valueFormat="MM/DD/YYYY"
            value={dueDate}
            onChange={(value) => setDueDate(toDateValue(value))}
            leftSection={<Calendar size={16} />}
            minDate={new Date()}
            clearable
          />

          <Divider label="Email Content" labelPosition="left" />

          {/* Merge Tag Buttons */}
          <Box>
            <Group gap="xs" mb={8}>
              <Tag size={14} />
              <Text size="sm" fw={500}>Insert Merge Tag</Text>
            </Group>
            <Group gap="xs">
              {MERGE_TAGS.map(({ tag, label }) => (
                <Tooltip key={tag} label={`Insert ${tag}`}>
                  <Badge
                    variant="light"
                    color="gray"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleInsertTag(tag)}
                  >
                    {label}
                  </Badge>
                </Tooltip>
              ))}
            </Group>
          </Box>

          <TextInput
            ref={subjectField.ref}
            label="Subject"
            placeholder="Enter email subject"
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
            onFocus={() => setLastFocusedField('subject')}
            onSelect={subjectField.trackCursor}
          />

          <Textarea
            ref={bodyField.ref}
            label="Message Body"
            placeholder="Enter email message"
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            onFocus={() => setLastFocusedField('body')}
            onSelect={bodyField.trackCursor}
            minRows={8}
            autosize
            maxRows={12}
          />

          {/* Preview Toggle */}
          <Group justify="flex-end">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<Eye size={14} />}
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </Group>

          {/* Preview Section - Shows actual email template */}
          {showPreview && (
            <Paper withBorder p={0} bg="gray.1" style={{ overflow: 'hidden' }}>
              {/* Preview Header */}
              <Box p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Preview (as seen by John Doe)
                </Text>
              </Box>

              {/* Email Metadata */}
              <Box p="md" bg="white" style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                <Text size="sm" c="dimmed">
                  From: <Text span fw={600} c="dark">Medley SGOS</Text> &lt;notifications@sgos.withmedley.com&gt;
                </Text>
                <Text size="sm" fw={600} mt={4}>
                  Complete Your {cohortName} Intake Form
                </Text>
              </Box>

              {/* Email Body Preview */}
              <Box p="md" bg="white">
                <Stack gap="md">
                  <Text size="sm">Hi John,</Text>

                  <Box>
                    <Text size="sm" fw={600} mb={4}>
                      Welcome to {cohortName}!
                    </Text>
                    <Text size="sm" c="dimmed">
                      We're excited to have you join us. Before we get started, please complete the intake form below. This helps us understand your goals, experience, and preferences so we can create the best possible experience for you.
                    </Text>
                  </Box>

                  {/* CTA Button Preview */}
                  <Box>
                    <Button
                      component="span"
                      color="dark"
                      style={{ backgroundColor: '#28463A', pointerEvents: 'none' }}
                    >
                      Complete Intake Form →
                    </Button>
                  </Box>

                  <Text size="sm" c="dimmed">
                    If you have any questions, feel free to reply to this email.
                  </Text>

                  <Box>
                    <Text size="sm">Best,</Text>
                    <Text size="sm">The Medley Team</Text>
                  </Box>
                </Stack>
              </Box>

              {/* Email Footer */}
              <Box p="sm" bg="gray.1" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
                <Text size="xs" c="dimmed" ta="center">
                  This email was sent to john@example.com
                </Text>
              </Box>
            </Paper>
          )}

          <Divider />

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleSendTestEmail}
              loading={sendingTest}
              disabled={!selectedFormId || sending || configuring}
            >
              Send Test Email
            </Button>
            <Tooltip label="Set up form tracking and copy email to clipboard">
              <Button
                variant="outline"
                onClick={handleConfigureAndCopy}
                loading={configuring}
                disabled={!selectedFormId || sending || sendingTest}
                leftSection={<Copy size={16} />}
              >
                Configure & Copy Email
              </Button>
            </Tooltip>
            <Button
              onClick={() => setConfirmOpened(true)}
              loading={sending}
              disabled={!selectedFormId || sendingTest || configuring}
            >
              Send Intake Forms
            </Button>
          </Group>

          {/* MED-646: Bulk send confirmation dialog */}
          <Modal
            opened={confirmOpened}
            onClose={() => setConfirmOpened(false)}
            title="Confirm Email Send"
            size="sm"
            centered
            zIndex={301}
          >
            <Stack gap="md">
              <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
                <Text size="sm">
                  You are about to send this email to{' '}
                  <Text component="span" fw={700}>
                    {recipientCount} participant{recipientCount !== 1 ? 's' : ''}
                  </Text>
                  . This action cannot be undone.
                </Text>
              </Alert>
              <Group justify="flex-end" gap="sm">
                <Button
                  variant="default"
                  onClick={() => setConfirmOpened(false)}
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  color="medleyGreen"
                  leftSection={<Send size={14} />}
                  onClick={() => {
                    setConfirmOpened(false);
                    handleSend();
                  }}
                  loading={sending}
                  disabled={sending}
                >
                  Confirm & Send
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      )}
    </Modal>
  );
}
